import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  StateBreakdownRow,
  StateCategory,
  MachineSummaryRow,
} from "../../services/ShiftAnalysisService";
import { STATE_COLORS, STATE_ORDER, formatDuration } from "./utils";
import { useStaggerIn } from "./anim";
import Panel from "./Panel";

interface Props {
  breakdown: Map<string, StateBreakdownRow[]>;
  machines: MachineSummaryRow[];
  focusedSn?: string | null;
  onHover?: (sn: string | null) => void;
  onPin?: (sn: string) => void;
}

const StateBreakdown: React.FC<Props> = ({
  breakdown,
  machines,
  focusedSn,
  onHover,
  onPin,
}) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    const order: { sn: string; name: string }[] = [];
    const seen = new Set<string>();
    for (const m of machines) {
      if (breakdown.has(m.machine_sn)) {
        order.push({ sn: m.machine_sn, name: m.machine_name });
        seen.add(m.machine_sn);
      }
    }
    for (const [sn, list] of breakdown) {
      if (!seen.has(sn)) order.push({ sn, name: list[0]?.machine_name ?? sn });
    }
    return order.map((o) => {
      const list = breakdown.get(o.sn) ?? [];
      const byCat = new Map<StateCategory, number>();
      let total = 0;
      for (const r of list) {
        byCat.set(
          r.state_category,
          (byCat.get(r.state_category) ?? 0) + r.total_seconds,
        );
        total += r.total_seconds;
      }
      const running = byCat.get("running") ?? 0;
      return {
        ...o,
        byCat,
        total,
        runningPct: total > 0 ? (running / total) * 100 : 0,
      };
    });
  }, [breakdown, machines]);

  const ref = useStaggerIn<HTMLDivElement>(".sa-sb-row", rows.length, {
    y: 10,
    stagger: 50,
  });

  if (rows.length === 0) return null;

  return (
    <Panel
      label={t("availabilityBreakdown", "Availability breakdown")}
      meta={t("howTheShiftSplit", "how each machine's shift split")}
    >
      <div ref={ref} className="flex flex-col gap-2.5">
        {rows.map((row) => {
          const dimmed = focusedSn != null && focusedSn !== row.sn;
          return (
            <button
              key={row.sn}
              type="button"
              onClick={() => onPin?.(row.sn)}
              onMouseEnter={() => onHover?.(row.sn)}
              onMouseLeave={() => onHover?.(null)}
              className="sa-sb-row w-full text-left transition-opacity"
              style={{ opacity: dimmed ? 0.45 : 1 }}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className="text-xs font-medium truncate"
                  style={{ color: "var(--text-primary)" }}
                >
                  {row.name}
                </span>
                <span
                  className="text-xs font-bold tabular-nums"
                  style={{ color: STATE_COLORS.running.color }}
                >
                  {Math.round(row.runningPct)}% {t("running", "running")}
                </span>
              </div>
              <div
                className="flex h-4 w-full rounded-full overflow-hidden"
                style={{ backgroundColor: "var(--border-primary)" }}
              >
                {STATE_ORDER.map((cat) => {
                  const secs = row.byCat.get(cat) ?? 0;
                  if (secs <= 0 || row.total <= 0) return null;
                  const pct = (secs / row.total) * 100;
                  const col = STATE_COLORS[cat];
                  return (
                    <div
                      key={cat}
                      style={{ width: `${pct}%`, backgroundColor: col.color }}
                      title={`${t(`state_${cat}`, col.label)}: ${formatDuration(secs)} (${Math.round(pct)}%)`}
                    />
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>
    </Panel>
  );
};

export default StateBreakdown;
